// BatchExportDialog（v0.64）—— 按零件打包「零件主数据 + 气泡图 +
// 作业指导书各工序 + 测量报告」。
//
// 用户原话（2026-10-17）：「增加个批量导出功能……需要有勾选的选项，勾选了就导出」。
// 所以这个对话框的全部职责就是三件事：
//   ① 让人**看见**自己勾了哪些零件、这些零件名下到底有几张图（preview）；
//   ② 让人**逐项勾**要导的内容（清单来自后端 `/batch-export/options`，
//      前端**不另写一份**工序清单 —— 否则加一道工序就要改两处）；
//   ③ 打包期间给出明确反馈（全厂 6 个零件全勾实测 ~25 秒，不是一瞬间的事），
//      结束后把「写了几个 / 跳过几个 / 失败几个」如实报出来。
//
// ★ 这一句必须留着：**失败项在包里，不在这个提示里**。前端只能报数，
//   具体是哪一项、为什么失败，写在 zip 内的 `_导出说明.txt`。
import { useEffect, useMemo, useState } from 'react';
import {
  exportBatch, fetchBatchExportOptions, previewBatchExport,
  type BatchExportOption, type BatchExportPlan,
} from '../api/client';
import { hasOwnStyle, loadBalloonStyle } from '../lib/balloonStyle';
import type { BalloonStyle } from '../lib/balloonStyle';

interface Props {
  open: boolean;
  partIds: number[];
  onClose: () => void;
  onToast: (msg: string, kind?: 'success' | 'error') => void;
}

type ToastKind = 'success' | 'error';

export function BatchExportDialog({ open, partIds, onClose, onToast }: Props) {
  const [options, setOptions] = useState<BatchExportOption[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<BatchExportPlan | null>(null);
  const [skipEmpty, setSkipEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const key = partIds.join(',');

  // 打开时：拉一次内容清单（默认勾选由后端给） + 预览这批零件的图纸
  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setErr('');
    setPlan(null);
    let alive = true;
    fetchBatchExportOptions()
      .then((o) => {
        if (!alive) return;
        setOptions(o.contents);
        setSel(new Set(o.contents.filter((c) => c.default).map((c) => c.key)));
      })
      .catch((e: any) => { if (alive) setErr(e.message || '读取导出内容清单失败'); });
    previewBatchExport(partIds)
      .then((p) => { if (alive) setPlan(p); })
      .catch((e: any) => { if (alive) setErr(e.message || '读取零件清单失败'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key]);

  /** 预计文件数。⚠️ 作业指导书一道工序可能有多份（v0.55），这里按 1 份估，
   *  界面上必须写「多份时更多」—— 报一个偏小的数还说是"预计"就是骗人。 */
  const est = useMemo(() => {
    const drawings = plan?.drawing_count ?? 0;
    let n = 0;
    if (sel.has('pm')) n += 1;
    if (sel.has('balloon')) n += drawings;
    if (sel.has('mr')) n += drawings;
    n += [...sel].filter((k) => k.startsWith('sip-')).length * drawings;
    return n;
  }, [sel, plan]);

  const toggle = (k: string) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  /** v0.77：样式按图纸存 ⇒ 打包时逐张取「本图纸单独设过的那份」。
   *  未单独设过的图纸不进来，由 `exportBatch` 的兜底样式统一处理。 */
  const styleOverrides = useMemo(() => {
    const map: Record<number, BalloonStyle> = {};
    for (const p of plan?.parts || []) {
      for (const d of p.drawings || []) {
        if (hasOwnStyle(d.id)) map[d.id] = loadBalloonStyle(d.id);
      }
    }
    return map;
  }, [plan]);

  const run = async () => {
    if (sel.size === 0) return;
    setBusy(true);
    setErr('');
    try {
      // v0.69：「关掉引线」要一起带上 —— 否则包里的气泡图 / SIP 图仍按默认画引线。
      // v0.77：样式按图纸存 ⇒ 有自定义的逐张覆盖，其余用兜底（未设过的继承值）。
      const res = await exportBatch(partIds, [...sel], skipEmpty,
                                   loadBalloonStyle(null), styleOverrides);
      const parts: string[] = [`已导出 ${res.ok} 个文件`];
      if (res.skipped) parts.push(`跳过 ${res.skipped} 项`);
      if (res.failed) parts.push(`失败 ${res.failed} 项`);
      const tail = res.failed
        ? ' —— 失败原因见包内《_导出说明.txt》'
        : '';
      onToast(`${parts.join('，')}：${res.filename}${tail}`,
              res.failed ? 'error' : 'success');
      onClose();
    } catch (e: any) {
      setErr(e.message || '导出失败');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const multiSip = [...sel].some((k) => k.startsWith('sip-'));

  return (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div className="modal batch-modal bx-modal" role="dialog" aria-modal="true"
           aria-label="批量导出">
        <div className="modal-head">
          <div>
            <div className="modal-title">批量导出（按零件打包）</div>
            <div className="modal-sub">
              已选 <strong>{partIds.length}</strong> 个零件
              {plan && <>
                {' · '}名下图纸 <strong>{plan.drawing_count}</strong> 张
                {' · '}每个零件一个文件夹
              </>}
            </div>
          </div>
          <button className="btn sm" onClick={onClose} disabled={busy} title="关闭">
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* ---------- ① 导出内容（勾了才导） ---------- */}
          <div className="bx-sec">
            <div className="bx-sec-title">
              导出内容 <span className="bx-sec-note">勾了才导，没勾的不进包</span>
            </div>
            <div className="bx-opts">
              {options.map((o) => (
                <label key={o.key} className={'bx-opt' + (sel.has(o.key) ? ' on' : '')}>
                  <input type="checkbox" checked={sel.has(o.key)}
                         disabled={busy} onChange={() => toggle(o.key)}
                         data-opt={o.key} />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
            <div className="bx-quick">
              <button className="btn sm" disabled={busy}
                      onClick={() => setSel(new Set(options.map((o) => o.key)))}>
                全选
              </button>
              <button className="btn sm" disabled={busy}
                      onClick={() => setSel(new Set())}>
                全不选
              </button>
              <button className="btn sm" disabled={busy}
                      title="只导检验现场要用的：气泡图 + 制程 + 成品 + 出货 + 测量报告"
                      onClick={() => setSel(new Set(
                        ['balloon', 'sip-02', 'sip-03', 'sip-04', 'mr']))}>
                检验常用
              </button>
            </div>
          </div>

          {/* ---------- ② 零件清单（让人确认勾的是谁） ---------- */}
          <div className="bx-sec">
            <div className="bx-sec-title">
              零件与图纸
              {plan && <span className="bx-sec-note">
                共 {plan.part_count} 个零件 / {plan.drawing_count} 张图纸
              </span>}
            </div>
            {!plan ? (
              <div className="bx-loading">正在读取零件清单…</div>
            ) : (
              <ul className="bx-parts">
                {plan.parts.map((p) => (
                  <li key={p.id}>
                    <span className="bx-part-name">{p.folder}</span>
                    <span className="bx-part-count">
                      {p.drawings.length
                        ? `${p.drawings.length} 张图纸`
                        : '无归户图纸'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {plan && plan.no_drawing.length > 0 && (
              <div className="bx-warn">
                ⚠ 这些零件名下还没有归户图纸，除了零件主数据不会产出别的文件：
                {plan.no_drawing.join('、')}
              </div>
            )}
          </div>

          {/* ---------- ③ 预计产出 ---------- */}
          {/* ⚠️ 这个数字只是**上限**，两个方向都可能偏：
                向上 —— 某道工序存了多份；
                向下 —— 0 行明细的空表会被跳过（本厂多数图纸的 -01 就是）。
              只写「更多」不写「更少」是不诚实的：用户按 8 个去数，拿到 7 个
              会以为丢了文件。 */}
          <div className="bx-est">
            预计 <strong>{est}</strong> 个文件
            {multiSip && <span className="bx-est-note">
              （工序存了多份时会更多）
            </span>}
            {skipEmpty && <span className="bx-est-note">
              （0 行明细的空表会被跳过，可能更少）
            </span>}
          </div>

          {busy && (
            <div className="bx-busy">
              正在逐个生成文件并打包，零件多时约需 10–30 秒，请不要关闭页面…
            </div>
          )}
          {err && <div className="bx-err">{err}</div>}
        </div>

        <div className="modal-foot bx-foot">
          <label className="bx-skip">
            <input type="checkbox" checked={skipEmpty} disabled={busy}
                   onChange={(e) => setSkipEmpty(e.target.checked)} />
            跳过 0 行明细的空表（如本厂多数图纸的 -01 原材料）
          </label>
          <div className="bx-foot-btns">
            <button className="btn" onClick={onClose} disabled={busy}>取消</button>
            <button className="btn primary" onClick={run}
                    disabled={busy || sel.size === 0 || !plan}>
              {busy ? '正在打包…' : `导出 ${est} 个文件`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
